import type { components } from '@/api/schema';

import { labelOf } from './humanize';

type Schemas = components['schemas'];

export type AuditOutcome = Schemas['AuditEvent']['outcome'];

// The schema carries the action as a plain string and the server adds new ones freely, so this
// is a curated list, not a contract. It names the actions where the humanized code would read
// badly (anything starting "AI", sign-in events, refusals); every other action falls through to
// humanize(), which already reads as a sentence: JOB_CREATED is "Job created".
export const AUDIT_ACTION_LABELS: Readonly<Record<string, string>> = {
  // Sign-in and accounts
  LOGIN_SUCCEEDED: 'Signed in',
  LOGIN_FAILED: 'Sign-in failed',
  LOGGED_OUT: 'Signed out',
  TOKEN_REFRESHED: 'Session renewed',
  TOKEN_REUSE_DETECTED: 'Reused session token blocked',
  SESSIONS_REVOKED: 'Signed out on every device',
  PASSWORD_CHANGED: 'Password changed',
  PASSWORD_RESET: 'Password reset by an administrator',
  ACCOUNT_LOCKED: 'Account locked after failed sign-ins',
  ACCOUNT_UNLOCKED: 'Account unlocked',
  ACCESS_DENIED: 'Access denied',
  USER_CREATED: 'User added',
  USER_UPDATED: 'User changed',
  USER_DELETED: 'User removed',
  TENANT_PROVISIONED: 'Workspace created',
  WORKSPACE_SETTINGS_CHANGED: 'Workspace settings changed',
  DISPATCH_SETTINGS_CHANGED: 'Dispatch settings changed',
  FINANCE_SETUP_CHANGED: 'Finance setup changed',
  SANDBOX_AI_DRAFT_SEEDED: 'Sample assistant draft added',

  // The assistant
  AI_REQUESTED: 'Assistant asked to read a record',
  AI_PROVIDER_REQUESTED: 'Question sent to the assistant',
  AI_BLOCKED: 'Assistant answer held back by policy',
  AI_PROPOSED: 'Assistant proposed a change',
  AI_APPROVED: 'Assistant change approved by a person',
  AI_REJECTED: 'Assistant change rejected by a person',
  AI_APPLY: 'Approved assistant change applied',
  AI_APPLIED: 'Approved assistant change applied',
  AI_CHAT_REQUESTED: 'Chat question asked',
  AI_CHAT_PROVIDER_REQUESTED: 'Chat question sent to the assistant',
  AI_CHAT_COMPLETED: 'Chat answer given',
  AI_CHAT_BLOCKED: 'Chat answer held back by policy',
  AI_CHAT_REVIEW_REQUESTED: 'Chat answer sent for safety review',
  AI_CHAT_REVIEWED: 'Chat answer reviewed',
  CONSULTATION_AI_REQUESTED: 'Consultation draft requested from the assistant',
  CONSULTATION_AI_DRAFTED: 'Assistant drafted for a consultation',
  CONSULTATION_AI_BLOCKED: 'Consultation draft held back by policy',
  CONSULTATION_AI_APPROVED: 'Consultation draft approved by a person',
  CONSULTATION_AI_REJECTED: 'Consultation draft rejected by a person',
  CONSULTATION_AI_APPLIED: 'Approved consultation draft applied',
  CONSULTATION_DRAFT_REVIEWED: 'Consultation draft reviewed by a person',
  FOLLOW_UP_AI_REQUESTED: 'Follow-up message requested from the assistant',
  FOLLOW_UP_AI_DRAFTED: 'Assistant drafted a follow-up message',
  FOLLOW_UP_AI_BLOCKED: 'Follow-up draft held back by policy',
  HANDOFF_AI_REQUESTED: 'Explanation requested from the assistant',
  HANDOFF_AI_VERIFIED: 'Assistant explanation checked',
  HANDOFF_AI_BLOCKED: 'Assistant explanation held back by policy',
  PROPOSAL_AI_REQUESTED: 'Explanation of findings requested from the assistant',
  PROPOSAL_AI_VERIFIED: 'Assistant explanation checked',
  PROPOSAL_AI_BLOCKED: 'Assistant explanation held back by policy',
  PROPOSAL_NARRATIVE_DRAFTED: 'Assistant drafted proposal wording',
  PROPOSAL_NARRATIVE_APPROVED: 'Proposal wording approved by a person',
  PROPOSAL_NARRATIVE_REJECTED: 'Proposal wording rejected by a person',
  REPLACEMENT_AI_REQUESTED: 'Worksheet summary requested from the assistant',
  REPLACEMENT_AI_VERIFIED: 'Assistant worksheet summary checked',
  REPLACEMENT_AI_BLOCKED: 'Assistant worksheet summary held back by policy',

  // Customers and sites
  CUSTOMER_CREATED: 'Customer added',
  CUSTOMER_UPDATED: 'Customer updated',
  CUSTOMER_DELETED: 'Customer removed',
  CUSTOMER_MERGED: 'Customers merged',
  CUSTOMER_MERGE_REFUSED: 'Customer merge refused',
  CUSTOMER_DUPLICATE_FLAGGED: 'Possible duplicate customer flagged',
  CUSTOMER_DUPLICATE_DISMISSED: 'Possible duplicate customer dismissed',
  CUSTOMERS_EXPORTED: 'Customer list exported',
  CUSTOMERS_IMPORTED: 'Customers imported',
  CUSTOMER_CONTACT_SUPPRESSED: 'Customer marked do not contact',
  CUSTOMER_OUTREACH_SUPPRESSED: 'Outreach to a customer stopped',
  CUSTOMER_SUPPRESSION_RECONCILED: 'Do-not-contact status reconciled',
  PROPERTY_CREATED: 'Property added',
  PROPERTY_UPDATED: 'Property updated',
  PROPERTY_DELETED: 'Property removed',
  EQUIPMENT_CREATED: 'Equipment added',
  EQUIPMENT_UPDATED: 'Equipment updated',
  EQUIPMENT_DELETED: 'Equipment removed',
  SERVICE_RECORD_ADDED: 'Service record added',
  NOTE_ADDED: 'Note added',
  TAG_CREATED: 'Tag created',

  // Jobs, dispatch and the field
  JOB_CREATED: 'Job created',
  JOB_ASSIGNED: 'Job assigned',
  JOB_STATUS_CHANGED: 'Job status changed',
  JOB_ATTACHMENT_ADDED: 'Attachment added to a job',
  JOB_CHECKLIST_CHANGED: 'Job checklist changed',
  JOB_COMPLETION_DATE_CONFIRMED: 'Job finish date confirmed',
  JOB_FIELD_HANDOFF_RAISED: 'Replacement raised from the field',
  JOB_FIELD_NOTE_ADDED: 'Field note added',
  JOB_FIELD_PHOTO_ADDED: 'Field photo added',
  JOB_FIELD_SIGNED: 'Customer signed off on a job',
  JOB_RECURRENCE_CREATED: 'Recurring jobs set up',
  JOB_RECURRENCE_STOPPED: 'Recurring jobs stopped',
  JOB_RECURRENCE_UNASSIGNED: 'Recurring jobs left unassigned',
  JOB_SKILL_SAVED: 'Job skill requirement saved',
  JOB_TYPE_SAVED: 'Job type saved',
  ROUTE_ADJUSTMENT_CONFIRMED: 'Route change confirmed',
  GEOCODE_BACKFILL_RUN: 'Missing address locations filled in',
  JOB_PACKAGE_ASSEMBLED: 'Installation package assembled',
  JOB_PACKAGE_ITEM_SETTLED: 'Installation package item decided',
  JOB_PACKAGE_WINDOW_CHOSEN: 'Installation window chosen',
  JOB_PACKAGE_SCHEDULED: 'Installation scheduled',
  JOB_PACKAGE_SCHEDULE_REFUSED: 'Installation scheduling refused',
  JOB_PACKAGE_OVERRIDDEN: 'Installation checks overridden by a manager',
  JOB_PACKAGE_OVERRIDE_REFUSED: 'Installation override refused',
  JOB_PACKAGE_TASK_DONE: 'Installation task completed',

  // Leads, deals and consultations
  LEAD_INTAKE_CREATED: 'Lead logged',
  LEAD_CONTACT_RECORDED: 'First contact recorded',
  LEAD_DISPOSITION_RECORDED: 'Lead outcome recorded',
  LEAD_OPPORTUNITY_CREATED: 'Opportunity created from a lead',
  LEAD_ROUTED: 'Lead routed',
  LEAD_OBJECTIVES_RECORDED: 'Customer objectives recorded',
  LEAD_ATTENTION_CHANGED: 'Lead attention flag changed',
  LEAD_CONFIGURATION_UPDATED: 'Intake settings updated',
  LEAD_REMINDER_CREATED: 'Lead reminder created',
  LEAD_REMINDER_REOPENED: 'Lead reminder reopened',
  LEAD_UNCONTACTED: 'Lead not contacted in time',
  INTAKE_DRAFT_REFUSED: 'Lead draft refused',
  SAFETY_ESCALATED: 'Safety concern escalated',
  OPPORTUNITY_CREATED: 'Opportunity created',
  OPPORTUNITY_UPDATED: 'Opportunity updated',
  OPPORTUNITY_CONTACT_LOGGED: 'Customer contact logged',
  OPPORTUNITY_TRANSITIONED: 'Opportunity moved to a new state',
  OPPORTUNITY_TRANSITION_REFUSED: 'Opportunity move refused',
  OPPORTUNITY_RESUMED: 'Closed opportunity resumed',
  OPPORTUNITY_NOT_SOLD: 'Opportunity closed without a sale',
  RESUME_TO_HUMAN_FOLLOW_UP: 'Opportunity resumed for follow-up',
  CONSULTATION_BOOKED: 'Consultation booked',
  CONSULTATION_STARTED: 'Consultation started',
  CONSULTATION_EDITED: 'Consultation notes updated',
  CONSULTATION_STEP: 'Consultation step reached',
  CONSULTATION_BRANCH: 'Consultation route chosen',
  CONSULTATION_DECISION_MAKER: 'Decision-maker presence recorded',
  CONSULTATION_COMPLETED: 'Consultation completed',
  CONSULTATION_CONFIGURED: 'Consultation settings changed',
  CONSULTATION_STATED_NEEDS_CONFIRMED: 'Stated needs confirmed',
  STATED_NEEDS_CONFIRMED: 'Stated needs confirmed',
  CONSULTATION_REMINDER_SCHEDULED: 'Consultation reminder scheduled',
  CONSULTATION_REMINDER_RETIRED: 'Consultation reminder retired',
  CONSULTATION_TASK_RESOLVED: 'Consultation task resolved',
  CONSULTATION_FOLLOW_UP_QUEUED: 'Follow-up message queued',
  CONSULTATION_RECAP_QUEUED: 'Recap message queued',
  CLOSING_CALL_OPENED: 'Presentation call opened',
  CLOSING_CALL_DISCLOSED: 'Disclosure covered on the call',
  CLOSING_CALL_DECIDED: 'Customer decision recorded',
  CLOSING_CALL_ACCEPTED: 'Customer accepted on the presentation call',
  CLOSING_CALL_DECISION_REFUSED: 'Customer decision refused',
  CLOSING_CALL_SUPERSEDED: 'Presentation call replaced',
  CLOSING_CALL_TRANSITION: 'Opportunity moved after the call',
  LOST_SALE_CONSENT_RECORDED: 'Permission to ask about a lost sale recorded',
  LOST_SALE_FEEDBACK_RECORDED: 'Lost-sale feedback recorded',

  // Proposals and replacement
  PROPOSAL_CREATED: 'Proposal created',
  PROPOSAL_VERSION_ADDED: 'Proposal version saved',
  PROPOSAL_VERSION_REFUSED: 'Proposal version refused',
  PROPOSAL_PRESENTED: 'Proposal presented to the customer',
  PROPOSAL_PRESENT_REFUSED: 'Presenting the proposal was refused',
  PROPOSAL_WITHDRAWN: 'Proposal withdrawn',
  PROPOSAL_APPROVAL_LINK_ISSUED: 'Customer link issued',
  PROPOSAL_APPROVAL_LINK_REVOKED: 'Customer link withdrawn',
  PROPOSAL_CUSTOMER_DECISION: 'Customer answered a proposal',
  PROPOSAL_ACCEPTED_BY_CUSTOMER: 'Customer accepted the proposal',
  PROPOSAL_DECLINED_BY_CUSTOMER: 'Customer declined or asked for more information',
  PROPOSAL_TEMPLATE_DRAFTED: 'Proposal template drafted',
  PROPOSAL_TEMPLATE_EDITED: 'Proposal template edited',
  PROPOSAL_TEMPLATE_PUBLISHED: 'Proposal template published',
  PROPOSAL_TEMPLATE_REVISED: 'Proposal template revised',
  PRICE_EXCEPTION_REQUESTED: 'Pricing exception requested',
  PRICE_EXCEPTION_APPROVED: 'Pricing exception approved',
  PRICE_EXCEPTION_REJECTED: 'Pricing exception rejected',
  PRICE_EXCEPTION_CARRIED_FORWARD: 'Approved pricing exception carried to the new version',
  REPLACEMENT_HANDOFF_SUBMITTED: 'Replacement handoff submitted',
  REPLACEMENT_HANDOFF_ROUTED: 'Replacement handoff routed',
  REPLACEMENT_DISAGREEMENT_ESCALATED: 'Customer disagreement escalated to a manager',
  REPLACEMENT_REVIEW_RESOLVED: 'Manager review resolved',
  WORKSHEET_APPROVED: 'Comparison worksheet approved',
  WORKSHEET_CUSTOMER_CHOICE: 'Customer choice recorded on the worksheet',
  WORKSHEET_HUMAN_ASSUMPTIONS_SAVED: 'Worksheet assumptions saved',
  FOLLOW_UP_SCHEDULED: 'Follow-up scheduled',
  FOLLOW_UP_OPENED: 'Follow-up opened',
  FOLLOW_UP_COMPLETED: 'Follow-up closed',
  FOLLOW_UP_ESCALATED: 'Follow-up escalated',
  FOLLOW_UP_PROBLEM_RAISED: 'Reported problem raised as a job',
  FOLLOW_UP_DRAFT_APPROVED: 'Follow-up message approved',
  OUTBOUND_QUEUED: 'Message queued to send',
  OUTBOUND_DELIVERED: 'Message delivered',
  OUTBOUND_UNKNOWN: 'Message delivery not confirmed, will retry',
  OUTBOUND_ATTEMPT_FAILED: 'Message delivery attempt failed',
  OUTBOUND_SUPPRESSED: 'Message not sent: customer asked not to be contacted',

  // Money, pricebook and stock
  INVOICE_CREATED: 'Invoice created',
  INVOICE_DRAFT_SAVED: 'Invoice draft saved',
  INVOICE_SENT: 'Invoice sent',
  INVOICE_VIEWED: 'Invoice viewed by the customer',
  INVOICE_OVERDUE: 'Invoice became overdue',
  INVOICE_VOIDED: 'Invoice voided',
  INVOICE_VOID_REFUSED: 'Voiding the invoice was refused',
  INVOICE_SEND_REFUSED: 'Sending the invoice was refused',
  INVOICE_SHARE_LINK_REISSUED: 'Invoice link reissued',
  PAYMENT_RECORDED: 'Payment recorded',
  PAYMENT_REFUSED: 'Payment refused',
  PAYROLL_PERIOD_OPENED: 'Payroll period opened',
  PAYROLL_WORK_APPROVED: 'Time worked approved for payroll',
  PAYROLL_COMPUTED: 'Payroll calculated',
  PAYROLL_COMPUTE_BLOCKED: 'Payroll calculation blocked',
  PAYROLL_ADJUSTED: 'Payroll adjusted',
  PAYROLL_PERIOD_LOCKED: 'Payroll period locked',
  PAYROLL_LOCK_BLOCKED: 'Locking the payroll period was blocked',
  COMPENSATION_APPENDED: 'Pay plan changed',
  PRICEBOOK_IMPORTED: 'Pricebook imported',
  PRICEBOOK_EXPORTED: 'Pricebook exported',
  SERVICE_ITEM_CREATED: 'Service added to the pricebook',
  SERVICE_ITEM_UPDATED: 'Pricebook service changed',
  SERVICE_ITEM_DELETED: 'Service removed from the pricebook',
  EQUIPMENT_ITEM_CREATED: 'Equipment added to the pricebook',
  EQUIPMENT_ITEM_UPDATED: 'Pricebook equipment changed',
  EQUIPMENT_ITEM_DELETED: 'Equipment removed from the pricebook',
  PRICE_TIER_CREATED: 'Price published',
  PRICE_CHANGED: 'Price replaced',
  PRICE_TIER_UPDATED: 'Price changed',
  PRICE_TIER_DELETED: 'Price withdrawn',
  PRICE_TIER_CLOSED: 'Price ended',
  PRICE_TIER_SETTLED: 'Price change refused: the days were already priced',
  PRICE_TIER_REFUSED: 'Price change refused',
  OFFERING_APPROVAL_CHANGED: 'Approved offering changed',
  OFFERING_CREATED_APPROVED: 'Offering added and approved for sale',
  REBATE_CREATED: 'Rebate recorded',
  REBATE_UPDATED: 'Rebate changed',
  REBATE_DELETED: 'Rebate withdrawn',
  COOKBOOK_ENTRY_CREATED: 'Pricing cookbook entry added',
  COOKBOOK_ENTRY_UPDATED: 'Pricing cookbook entry revised',
  COOKBOOK_ENTRY_DELETED: 'Pricing cookbook entry removed',
  GROSS_PROFIT_TARGET_SET: 'Margin target set',
  GROSS_PROFIT_TARGET_CLEARED: 'Margin target removed',
  MATERIAL_CREATED: 'Material added to the stock list',
  MATERIAL_UPDATED: 'Material changed',
  MATERIAL_DELETED: 'Material removed',
  STOCK_MOVED: 'Stock moved',
  STOCK_DELIVERY_RECEIVED: 'Delivery received into stock',
  STOCK_MOVEMENT_REFUSED: 'Stock movement refused',
  STOCK_RECONCILED: 'Stock count reconciled',
  STOCK_DEDUCTED_FOR_DOCUMENT: 'Stock used on an invoice',
  STOCK_RESTORED_FOR_DOCUMENT: 'Stock returned from an invoice',
  MATERIALS_IMPORTED: 'Materials imported',
  MATERIALS_EXPORTED: 'Materials exported',

  // Team
  TEAM_PROFILE_SAVED: 'Team profile saved',
  MEMBER_SKILL_SAVED: 'Team member skill saved',
  SKILL_SAVED: 'Skill saved',
  DEPARTMENT_SAVED: 'Department saved',
  TIME_OFF_REQUESTED: 'Time off requested',
  TIME_OFF_MARKED_APPROVED: 'Time off marked and approved',
  TIME_OFF_APPROVED: 'Time off approved',
  TIME_OFF_REJECTED: 'Time off rejected',
  PERSON_CREATED: 'Person added',
  PERSON_UPDATED: 'Person updated',
};

// Audit entity types arrive as SCREAMING_CASE from some modules and PascalCase from others.
const AUDIT_ENTITY_LABELS: Readonly<Record<string, string>> = {
  AiRequest: 'Assistant request',
  AiProposedAction: 'Assistant proposal',
  Tenant: 'Workspace',
};

export const AUDIT_OUTCOME_LABELS: Record<AuditOutcome, string> = {
  SUCCEEDED: 'Succeeded',
  DENIED: 'Denied',
  FAILED: 'Failed',
};

export function auditActionLabel(action: string | null | undefined): string {
  return labelOf(AUDIT_ACTION_LABELS, action);
}

export function auditEntityLabel(entityType: string | null | undefined): string {
  return labelOf(AUDIT_ENTITY_LABELS, entityType);
}

export function auditOutcomeLabel(outcome: string | null | undefined): string {
  return labelOf(AUDIT_OUTCOME_LABELS, outcome);
}
